<script setup>
// Config do node "HTTP Request" — uma ferramenta de API externa que o agente
// pode chamar (consultar/enviar dados). É salvo no node e enviado ao runtime
// como http_tool (custom_tools.http_tools). O LLM lê a descrição pra saber
// quando chamar e quais parâmetros passar (placeholders {param} na URL/body).
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import agentsAPI from 'dashboard/api/agents/agents';

const props = defineProps({
  data: { type: Object, required: true },
});
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const AUTH = ['none', 'bearer', 'api_key', 'basic'];

const up = patch => emit('update', patch);
const onField = (key, event) => up({ [key]: event.target.value });

const hasBody = () =>
  ['POST', 'PUT', 'PATCH'].includes(props.data.method || 'GET');
const showAuthValue = () => (props.data.authType || 'none') !== 'none';

// Testar: faz a requisição configurada e mostra status + resposta, antes de
// publicar o fluxo.
const testing = ref(false);
const testResult = ref(null);
const testRequest = async () => {
  if (!props.data.endpoint) {
    useAlert(t('AI_AGENTS.FLOW.HTTP.TEST.NO_URL'));
    return;
  }
  testing.value = true;
  testResult.value = null;
  try {
    const { data } = await agentsAPI.testHttpTool({
      method: props.data.method || 'GET',
      endpoint: props.data.endpoint,
      headers: props.data.headers,
      body_template: props.data.bodyTemplate,
      auth_type: props.data.authType,
      auth_value: props.data.authValue,
      response_path: props.data.responsePath,
    });
    testResult.value = data;
  } catch (e) {
    testResult.value = { success: false, error: 'request_failed' };
  } finally {
    testing.value = false;
  }
};
</script>

<template>
  <div class="p-4">
    <div class="mb-1 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="i-lucide-globe text-n-slate-11" />
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.HTTP.TITLE') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>
    <p class="mb-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.HTTP.SUBTITLE') }}
    </p>

    <!-- Nome do tool -->
    <label class="mb-1 block text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.HTTP.TOOL_NAME') }}
    </label>
    <input
      :value="data.toolName"
      :placeholder="t('AI_AGENTS.FLOW.HTTP.TOOL_NAME_PH')"
      class="mb-3 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
      @input="onField('toolName', $event)"
    />

    <!-- Descrição (o LLM lê isto) -->
    <label class="mb-1 block text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.HTTP.DESC') }}
    </label>
    <textarea
      :value="data.description"
      rows="3"
      :placeholder="t('AI_AGENTS.FLOW.HTTP.DESC_PH')"
      class="mb-1 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
      @input="onField('description', $event)"
    />
    <p class="mb-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.HTTP.DESC_HINT') }}
    </p>

    <!-- Método + URL -->
    <div class="mb-3 flex items-center justify-between gap-2">
      <span class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.HTTP.METHOD') }}
      </span>
      <select
        :value="data.method || 'GET'"
        class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1 text-sm"
        @change="onField('method', $event)"
      >
        <option v-for="m in METHODS" :key="m" :value="m">{{ m }}</option>
      </select>
    </div>

    <label class="mb-1 block text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.HTTP.URL') }}
    </label>
    <input
      :value="data.endpoint"
      :placeholder="t('AI_AGENTS.FLOW.HTTP.URL_PH')"
      class="mb-1 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 font-mono text-[13px]"
      @input="onField('endpoint', $event)"
    />
    <p class="mb-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.HTTP.URL_HINT') }}
    </p>

    <!-- Headers (JSON) -->
    <label class="mb-1 block text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.HTTP.HEADERS') }}
    </label>
    <textarea
      :value="data.headers"
      rows="2"
      :placeholder="t('AI_AGENTS.FLOW.HTTP.HEADERS_PH')"
      class="mb-3 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 font-mono text-[12px]"
      @input="onField('headers', $event)"
    />

    <!-- Body (POST/PUT/PATCH) -->
    <template v-if="hasBody()">
      <label class="mb-1 block text-xs text-n-slate-10">
        {{ t('AI_AGENTS.FLOW.HTTP.BODY') }}
      </label>
      <textarea
        :value="data.bodyTemplate"
        rows="3"
        :placeholder="t('AI_AGENTS.FLOW.HTTP.BODY_PH')"
        class="mb-3 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 font-mono text-[12px]"
        @input="onField('bodyTemplate', $event)"
      />
    </template>

    <!-- Auth -->
    <div class="mb-3 flex items-center justify-between gap-2">
      <span class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.HTTP.AUTH') }}
      </span>
      <select
        :value="data.authType || 'none'"
        class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1 text-sm"
        @change="onField('authType', $event)"
      >
        <option v-for="a in AUTH" :key="a" :value="a">
          {{ t(`AI_AGENTS.FLOW.HTTP.AUTH_OPTS.${a}`) }}
        </option>
      </select>
    </div>
    <input
      v-if="showAuthValue()"
      :value="data.authValue"
      :placeholder="t('AI_AGENTS.FLOW.HTTP.AUTH_VALUE_PH')"
      class="mb-3 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 font-mono text-[13px]"
      @input="onField('authValue', $event)"
    />

    <!-- Caminho da resposta -->
    <label class="mb-1 block text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.HTTP.RESPONSE_PATH') }}
    </label>
    <input
      :value="data.responsePath"
      :placeholder="t('AI_AGENTS.FLOW.HTTP.RESPONSE_PATH_PH')"
      class="mb-1 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 font-mono text-[13px]"
      @input="onField('responsePath', $event)"
    />
    <p class="mb-4 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.HTTP.RESPONSE_PATH_HINT') }}
    </p>

    <!-- Testar requisição -->
    <button
      class="mb-2 flex w-full items-center justify-center gap-2 rounded-lg border border-n-brand py-2 text-sm font-medium text-n-brand hover:bg-n-brand/10 disabled:opacity-60"
      :disabled="testing"
      @click="testRequest"
    >
      <span
        :class="testing ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-zap'"
      />
      {{
        testing
          ? t('AI_AGENTS.FLOW.HTTP.TEST.TESTING')
          : t('AI_AGENTS.FLOW.HTTP.TEST.BUTTON')
      }}
    </button>
    <div
      v-if="testResult"
      class="mb-3 rounded-lg border px-3 py-2 text-xs"
      :class="
        testResult.success
          ? 'border-n-teal-6 bg-n-teal-2 text-n-teal-11'
          : 'border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11'
      "
    >
      <p class="font-medium">
        <span v-if="testResult.success">
          {{ t('AI_AGENTS.FLOW.HTTP.TEST.OK', { status: testResult.status }) }}
        </span>
        <span v-else-if="testResult.status">
          {{
            t('AI_AGENTS.FLOW.HTTP.TEST.HTTP_FAIL', {
              status: testResult.status,
            })
          }}
        </span>
        <span v-else>{{ t('AI_AGENTS.FLOW.HTTP.TEST.UNREACHABLE') }}</span>
      </p>
      <p v-if="testResult.extracted" class="mt-1 break-words">
        {{ t('AI_AGENTS.FLOW.HTTP.TEST.EXTRACTED') }}:
        {{ testResult.extracted }}
      </p>
      <pre
        v-if="testResult.body"
        class="mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-words text-[11px] opacity-80"
        >{{ testResult.body }}</pre
      >
    </div>

    <button
      class="w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
